import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { generateUniqueId } from '../../../utils/generateUniqueId';
/**
 * The retired imperative sheet stack, kept working on top of the single overlay array while its
 * remaining callers migrate to showModal. Pages appear and disappear within the caller's own tick,
 * which is the timing the stack it replaced guaranteed.
 */ export function useImperativeSheetPages(param) {
    let { entries, sheetRuns, nextZIndexOrder, closeRunWithSlideOut } = param;
    function appendPageInOneCommit(config, sheetRunId) {
        const { content, id, zIndexOrder } = config, sheetPage = _object_without_properties(config, [
            "content",
            "id",
            "zIndexOrder"
        ]);
        const entryId = generateUniqueId('bottom-sheet-page');
        if (id != null) sheetRuns.rememberCallerPageId(id, entryId);
        sheetRuns.joinOrStartRun(sheetRunId, entryId);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const entry = {
            // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- the retired sheet-page API never carried a BI name, so its pages are untracked by design
            noBi: true,
            content: ()=>/*#__PURE__*/ _jsx(_Fragment, {
                    children: content
                }),
            id: entryId,
            isOpen: true,
            resolve: ()=>{},
            dismissModal: ()=>{},
            zIndexOrder: zIndexOrder !== null && zIndexOrder !== void 0 ? zIndexOrder : nextZIndexOrder(),
            presentation: 'sheet',
            sheetRunId,
            sheetPage
        };
        entries.getUpdateEntries()((previousEntries)=>previousEntries.concat(entry));
    }
    function dropPagesReportingRemovalNow(shouldDrop) {
        const dropped = entries.readEntriesIncludingThisTick().filter(shouldDrop);
        if (dropped.length === 0) return;
        const droppedIds = new Set(dropped.map((entry)=>entry.id));
        sheetRuns.getForgetEntries()([
            ...droppedIds
        ]);
        dropped.forEach((entry)=>{
            var _entry_sheetPage_onRemoved, _entry_sheetPage;
            entry.settled = true;
            (_entry_sheetPage = entry.sheetPage) === null || _entry_sheetPage === void 0 ? void 0 : (_entry_sheetPage_onRemoved = _entry_sheetPage.onRemoved) === null || _entry_sheetPage_onRemoved === void 0 ? void 0 : _entry_sheetPage_onRemoved.call(_entry_sheetPage);
            entry.resolve(undefined);
        });
        // WHY: the updater stays pure — the ref bookkeeping and the caller callbacks run around it, not inside it, or a deferred flush reorders them.
        entries.getUpdateEntries()((previousEntries)=>previousEntries.filter((entry)=>!droppedIds.has(entry.id)));
    }
    return {
        getHasOpenBottomSheet: ()=>sheetRuns.readOpenRunId() != null,
        showBottomSheet: (config)=>{
            dropPagesReportingRemovalNow((entry)=>entry.presentation === 'sheet');
            appendPageInOneCommit(config, generateUniqueId('sheet-run'));
        },
        pushBottomSheetPage: (config)=>{
            var _sheetRuns_readOpenRunId;
            return appendPageInOneCommit(config, (_sheetRuns_readOpenRunId = sheetRuns.readOpenRunId()) !== null && _sheetRuns_readOpenRunId !== void 0 ? _sheetRuns_readOpenRunId : generateUniqueId('sheet-run'));
        },
        popBottomSheetPage: ()=>{
            const lastPageId = sheetRuns.lastEntryIdOfOpenRun();
            if (lastPageId) dropPagesReportingRemovalNow((entry)=>entry.id === lastPageId);
        },
        removeBottomSheetPage: (callerPageId)=>{
            var _sheetRuns_entryIdForCallerPageId;
            const entryId = (_sheetRuns_entryIdForCallerPageId = sheetRuns.entryIdForCallerPageId(callerPageId)) !== null && _sheetRuns_entryIdForCallerPageId !== void 0 ? _sheetRuns_entryIdForCallerPageId : callerPageId;
            dropPagesReportingRemovalNow((entry)=>entry.presentation === 'sheet' && entry.id === entryId);
        },
        hideBottomSheet: ()=>{
            const closedRunId = sheetRuns.closeOpenRun();
            if (closedRunId != null) closeRunWithSlideOut(closedRunId);
        }
    };
}
